import type { ComponentProgressDetailView, ComponentStatus } from '@/types/coaching'

export interface EssentialCheckProgress {
  score?: number | null
}

export interface WorkbookGradeProgress {
  itemKind: string
  itemNo: number
  isCorrect?: boolean | null
}

export const REQUIRED_WORKBOOK_PROBLEM_NOS = [1, 2, 3, 4, 5] as const

export function incompleteEssentialChecks<T extends EssentialCheckProgress>(checks: T[]): T[] {
  return checks.filter((check) => check.score == null)
}

export function incompleteWorkbookProblemNos(
  grades: WorkbookGradeProgress[],
  enabled = true,
): number[] {
  if (!enabled) return []
  const completed = new Set(
    grades
      .filter((grade) => grade.itemKind === 'PROBLEM' && grade.isCorrect != null)
      .map((grade) => grade.itemNo),
  )
  return REQUIRED_WORKBOOK_PROBLEM_NOS.filter((itemNo) => !completed.has(itemNo))
}

export function isRequiredBuildupGradeItem(step: string, itemNo: number): boolean {
  // STEP 4의 #2 이후 항목은 #1과 함께 보여 주는 모범답안 데이터이며 별도 채점 대상이 아니다.
  const stepNumber = step.match(/\d+/)?.[0]
  return stepNumber !== '4' || itemNo === 1
}

export function componentNeedsTeacherGrading(component: ComponentProgressDetailView): boolean {
  if (component.status !== 'completed') return false

  if (component.type === 'buildup') {
    return component.buildup?.steps.some((step) => step.items.some((item) => (
      isRequiredBuildupGradeItem(step.step, item.number) && !item.grade
    ))) ?? false
  }

  if (component.type === 'quiz') {
    if (!component.quiz || component.quiz.solveMode === 'test') return false
    return component.quiz.questions.some((question) => (
      question.isCorrect == null
      && (question.isOfflineCompleted || hasMeaningfulResponse(question.studentAnswer))
    ))
  }

  if (component.type === 'video' && component.video?.pauseEnabled) {
    const responsesByPausePoint = new Map(
      (component.video.pauseQuizResponses ?? []).map((response) => [
        `${response.quizId}:${response.pauseTimeMs}`,
        response,
      ]),
    )
    return (component.video.pausePoints ?? []).some((point) => {
      const response = responsesByPausePoint.get(`${point.quizId}:${point.pauseTimeMs}`)
      return response?.isCorrect == null
    })
  }

  return false
}

function hasMeaningfulResponse(value: unknown): boolean {
  if (value === null || value === undefined) return false
  if (Array.isArray(value)) return value.some(hasMeaningfulResponse)
  if (typeof value === 'string') return value.trim().length > 0
  if (typeof value === 'number' || typeof value === 'boolean') return true
  if (typeof value === 'object') return Object.keys(value).length > 0
  return false
}

export function understandingStepStatus(
  checks: EssentialCheckProgress[] | undefined,
): Exclude<ComponentStatus, 'skipped'> {
  if (!checks) return 'not_started'
  if (incompleteEssentialChecks(checks).length === 0) return 'completed'
  return checks.some((check) => check.score != null) ? 'in_progress' : 'not_started'
}

export function workbookStepStatus(
  grades: WorkbookGradeProgress[] | undefined,
): Exclude<ComponentStatus, 'skipped'> {
  if (!grades) return 'not_started'
  const incomplete = incompleteWorkbookProblemNos(grades)
  if (incomplete.length === 0) return 'completed'
  return incomplete.length < REQUIRED_WORKBOOK_PROBLEM_NOS.length ? 'in_progress' : 'not_started'
}
